// libraries
import type { DealHistory, DealHistoryState, StateTree } from '@makemydeal/dr-dash-types';
import { type Offer, type PaymentTerm, type SpecialPaymentFormulaType } from '@makemydeal/dr-platform-types';

import { getDealXgVersion } from './offerInfoSelectors';

export const getDealHistory = (state: StateTree): DealHistoryState => {
    return state.dealHistory || { offers: [] };
};

export const getDealHistoryOffers = (state: StateTree): DealHistory[] => {
    const dealHistory = getDealHistory(state);
    return dealHistory.offers || [];
};

export const getDefaultDealHistory = (state: StateTree) => {
    const deals = getDealHistory(state);
    return deals.offers && deals.offers.length
        ? deals.offers.reduce((prev: any, curr: any) => {
              return Date.parse(curr.offer.createdOn as string) < Date.parse(prev.offer.createdOn as string) ? curr : prev;
          })
        : ({} as DealHistory);
};

export const getDealHistoryIsLoading = (state: StateTree): boolean => {
    const dealHistory = getDealHistory(state);
    return dealHistory.isLoading || false;
};

export const isPendingReviewDetecting = (state: StateTree): boolean => getDealHistory(state).pendingReviewDetecting || false;

// `isLoading` is absent from the initial state, so undefined means no fetch was issued while false means
// one finished, success or failure alike. getDealHistoryIsLoading flattens that distinction away.
export const hasDealHistorySettled = (state: StateTree): boolean => getDealHistory(state).isLoading === false;

export const isDealHistoryDrawerOpen = (state: StateTree): boolean => {
    const dealHistory = getDealHistory(state);
    return dealHistory.drawerOpen || false;
};

export const getMakeCurrentSuccess = (state: StateTree): boolean => {
    const dealHistory = getDealHistory(state);
    return dealHistory.makeCurrentSuccess || false;
};

// Offers from MV should be considered dealer offers
export const getDealerOffers = (state: StateTree): DealHistory[] => {
    const deals = getDealHistoryOffers(state);
    const dealerOffers = deals.filter((deal) => deal.offer.offerSource?.toLowerCase() === 'dealmaker');
    return dealerOffers;
};

// Offers generated from either Showroom or AMD should be considered shopper offers
export const getShopperOffers = (state: StateTree): DealHistory[] => {
    const deals = getDealHistoryOffers(state);
    const shopperOffers = deals.filter(
        (deal) => deal.offer.offerSource?.toLowerCase() === 'shopper' || deal.offer.offerSource?.toLowerCase() === 'dealer'
    );
    return shopperOffers;
};

const getLatestOffer = (offers: DealHistory[]) => {
    const latestOffer = offers
        .filter((deal) => {
            // TODO: DASH - offers that have a falsy createdOn value can't be timelined, so are filtered out
            return !!deal.offer.createdOn;
        })
        .reduce((previousOffer: DealHistory, currentOffer: DealHistory) => {
            const previousDate = new Date(previousOffer.offer?.createdOn as string);
            const currentDate = new Date(currentOffer.offer.createdOn as string);
            return previousDate > currentDate ? previousOffer : currentOffer;
        }, {} as DealHistory);
    return Object.keys(latestOffer).length ? latestOffer : undefined;
};

export const getLatestDealerOffer = (state: StateTree): DealHistory | undefined => {
    const dealerOffers = getDealerOffers(state);
    const latestDealerOffer = getLatestOffer(dealerOffers);
    return latestDealerOffer;
};

export const getLatestShopperOffer = (state: StateTree): DealHistory | undefined => {
    const shopperOffers = getShopperOffers(state);
    const latestShopperOffer = getLatestOffer(shopperOffers);
    return latestShopperOffer;
};

export const getSelectedDealHistory = (state: StateTree): DealHistory[] => {
    const dealHistory = getDealHistory(state);
    return dealHistory.selectedDeals || [];
};

export const getSelectedDealBySelectedColumn = (state: StateTree): DealHistory | undefined => {
    const dealHistory = getDealHistory(state);
    return (dealHistory.selectedDeals as DealHistory[]).find((deal) => deal.isSelected);
};

export const getDealScenarioCount = (deal: DealHistory): number | undefined => {
    return deal?.dealXgDetails?.scenarioCount;
};

export const getScenariosByDealVersion = (state: StateTree, dealVersion?: string): DealHistory[] => {
    if (!dealVersion) {
        return [];
    }
    const dealHistory = getDealHistory(state);
    return dealHistory.scenarios?.[dealVersion] || [];
};

export const getDealHistoryScenarioDrawerDeal = (state: StateTree): DealHistory | undefined => {
    const dealHistory = getDealHistory(state);
    return dealHistory.dealHistoryScenarioDrawerDeal;
};

export const getIsCurrentOfferDealer = (state: StateTree): boolean => {
    const offerInfoDxgVersion = getDealXgVersion(state);
    const dealerOffer = getLatestDealerOffer(state);
    const latestDealerOfferDxgVersion = dealerOffer?.offer?.dealXgVersion;

    if (!latestDealerOfferDxgVersion || !offerInfoDxgVersion) {
        return false;
    }

    return latestDealerOfferDxgVersion === offerInfoDxgVersion;
};

export const getIsCurrentOfferShopper = (state: StateTree): boolean => {
    const offerInfoDxgVersion = getDealXgVersion(state);
    const shopperOffer = getLatestShopperOffer(state);
    const latestShopperOfferDxgVersion = shopperOffer?.offer?.dealXgVersion;

    if (!latestShopperOfferDxgVersion || !offerInfoDxgVersion) {
        return false;
    }

    return latestShopperOfferDxgVersion === offerInfoDxgVersion;
};

/**
 * Returns true when at least one dealer-sourced history offer has a dealXgVersion
 * different from the current offerInfo.dealXgVersion.
 * This indicates the dealer has previously published a version from MV.
 */
export const hasPriorMvPublishedVersion = (state: StateTree): boolean => {
    const currentDxgVersion = getDealXgVersion(state);
    const dealerOffers = getDealerOffers(state);
    if (!currentDxgVersion || dealerOffers.length === 0) return false;
    return dealerOffers.some((deal) => deal.offer.dealXgVersion !== currentDxgVersion);
};

export const getDealHistoryDrawerOpen = (state: StateTree): boolean => {
    const dealHistory = getDealHistory(state);
    return dealHistory.drawerOpen || false;
};

export const getOfferPayment = (offer: Offer): PaymentTerm => {
    return offer?.payment || {};
};

export const getSpecialPaymentFormula = (offerPayment: PaymentTerm): SpecialPaymentFormulaType | undefined => {
    return offerPayment?.specialPaymentFormula;
};

export const comparableTermPaymentDetail = (offerPayment: PaymentTerm) => {
    return offerPayment?.comparableTermPaymentDetail;
};

export const firstTermPaymentDetail = (offerPayment: PaymentTerm) => {
    return offerPayment?.firstTermPaymentDetail;
};

export const firstPaymentTerm = (offerPayment: PaymentTerm) => {
    const firstPayment = firstTermPaymentDetail(offerPayment);
    return firstPayment?.term;
};

export const firstPaymentAmount = (offerPayment: PaymentTerm) => {
    const firstPayment = firstTermPaymentDetail(offerPayment);
    return firstPayment?.paymentAmount;
};

export const secondTermPaymentDetail = (offerPayment: PaymentTerm) => {
    return offerPayment?.secondTermPaymentDetail;
};

export const secondPaymentTerm = (offerPayment: PaymentTerm) => {
    const secondPayment = secondTermPaymentDetail(offerPayment);
    return secondPayment?.term;
};

export const secondPaymentAmount = (offerPayment: PaymentTerm) => {
    const secondPayment = secondTermPaymentDetail(offerPayment);
    return secondPayment?.paymentAmount;
};
